/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useAudioPreview.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for managing audio preview playback.
    Handles play/pause, blob URL management, and audio element lifecycle.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useRef, useCallback } from 'react';
import { buildApiUrl, getApiHeaders } from '../../../config/api';
import { useNotifications } from '../../../contexts/NotificationContext';

/**
 * Hook for managing audio preview
 */
export function useAudioPreview() {
  const { showError } = useNotifications();

  // Music audio state
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playingFileIndex, setPlayingFileIndex] = useState<number | string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isPausingManuallyRef = useRef(false);

  // Chime audio state
  const [chimeAudioUrl, setChimeAudioUrl] = useState<string | null>(null);
  const [isChimePlaying, setIsChimePlaying] = useState(false);
  const [playingChimeIndex, setPlayingChimeIndex] = useState<number | string | null>(null);
  const chimeAudioRef = useRef<HTMLAudioElement | null>(null);

  // Stream audio state
  const [isStreamPlaying, setIsStreamPlaying] = useState(false);
  const streamAudioRef = useRef<HTMLAudioElement | null>(null);

  // Play file from preview URL
  const playFile = useCallback(
    (previewUrl: string, index: number | string) => {
      // Stop current playback
      if (audioRef.current && isPlaying) {
        audioRef.current.pause();
      }

      // Clean up previous blob URL if different
      if (audioUrl && audioUrl.startsWith('blob:') && audioUrl !== previewUrl) {
        try {
          URL.revokeObjectURL(audioUrl);
        } catch (e) {
          // Ignore errors
        }
      }

      setAudioUrl(previewUrl);
      setPlayingFileIndex(index);
      setIsPlaying(true);
    },
    [audioUrl, isPlaying],
  );

  // Play file from server
  const playServerFile = useCallback(
    async (mohId: number | string, fileName: string, fileKey: string) => {
      if (isPlaying && playingFileIndex === fileKey) {
        // Pause if already playing
        if (audioRef.current) {
          audioRef.current.pause();
          setIsPlaying(false);
          setPlayingFileIndex(null);
          if (audioUrl && audioUrl.startsWith('blob:')) {
            URL.revokeObjectURL(audioUrl);
          }
          setAudioUrl(null);
        }
        return;
      }

      try {
        // Stop any currently playing audio
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.src = '';
        }

        // Clean up previous blob URL
        if (audioUrl && audioUrl.startsWith('blob:')) {
          URL.revokeObjectURL(audioUrl);
        }

        const url = buildApiUrl(`/api/telephony/moh-sources/${mohId}/file/${encodeURIComponent(fileName)}`);
        const response = await fetch(url, {
          method: 'GET',
          headers: getApiHeaders(),
        });

        if (!response.ok) {
          const errorText = await response.text();
          let errorData;
          try {
            errorData = JSON.parse(errorText);
          } catch {
            errorData = { error: errorText || response.statusText };
          }
          throw new Error(
            errorData.error || errorData.message || `Failed to load audio: ${response.statusText}`,
          );
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        setAudioUrl(blobUrl);
        setPlayingFileIndex(fileKey);
        setIsPlaying(true);
      } catch (error) {
        showError(error.message || 'Failed to load audio file');
        setIsPlaying(false);
        setPlayingFileIndex(null);
      }
    },
    [audioUrl, isPlaying, playingFileIndex, showError],
  );

  // Play chime file from server
  const playChimeServerFile = useCallback(
    async (mohId: number | string, fileName: string, fileKey: string) => {
      if (isChimePlaying && playingChimeIndex === fileKey) {
        // Pause if already playing
        if (chimeAudioRef.current) {
          chimeAudioRef.current.pause();
          setIsChimePlaying(false);
          setPlayingChimeIndex(null);
          if (chimeAudioUrl && chimeAudioUrl.startsWith('blob:')) {
            URL.revokeObjectURL(chimeAudioUrl);
          }
          setChimeAudioUrl(null);
        }
        return;
      }

      try {
        // Stop any currently playing chime
        if (chimeAudioRef.current) {
          chimeAudioRef.current.pause();
          chimeAudioRef.current.src = '';
        }

        // Clean up previous blob URL
        if (chimeAudioUrl && chimeAudioUrl.startsWith('blob:')) {
          URL.revokeObjectURL(chimeAudioUrl);
        }

        const url = buildApiUrl(`/api/telephony/moh-sources/${mohId}/file/${encodeURIComponent(fileName)}`);
        const response = await fetch(url, {
          method: 'GET',
          headers: getApiHeaders(),
        });

        if (!response.ok) {
          const errorText = await response.text();
          let errorData;
          try {
            errorData = JSON.parse(errorText);
          } catch {
            errorData = { error: errorText || response.statusText };
          }
          throw new Error(
            errorData.error || errorData.message || `Failed to load chime audio: ${response.statusText}`,
          );
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        setChimeAudioUrl(blobUrl);
        setPlayingChimeIndex(fileKey);
        setIsChimePlaying(true);
      } catch (error) {
        showError(error.message || 'Failed to load chime audio file');
        setIsChimePlaying(false);
        setPlayingChimeIndex(null);
      }
    },
    [chimeAudioUrl, isChimePlaying, playingChimeIndex, showError],
  );

  // Test stream
  const testStream = useCallback(
    (streamUrl: string) => {
      if (isStreamPlaying) {
        // Stop stream
        isPausingManuallyRef.current = true;
        setIsStreamPlaying(false);
        if (streamAudioRef.current) {
          streamAudioRef.current.pause();
          streamAudioRef.current.src = '';
        }
        setTimeout(() => {
          isPausingManuallyRef.current = false;
        }, 200);
        return;
      }

      // Validate URL
      try {
        new URL(streamUrl);
      } catch {
        showError('Invalid URL format. Please enter a valid HTTP(S) URL');
        return;
      }

      // Stop any file audio playback
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        setIsPlaying(false);
        setPlayingFileIndex(null);
      }

      // Clean up blob URL if exists
      if (audioUrl && audioUrl.startsWith('blob:')) {
        URL.revokeObjectURL(audioUrl);
        setAudioUrl(null);
      }

      // Create or get stream audio element
      if (!streamAudioRef.current) {
        const audio = new Audio();
        audio.crossOrigin = 'anonymous';

        audio.onended = () => {
          setIsStreamPlaying(false);
        };

        audio.onerror = () => {
          if (!isPausingManuallyRef.current) {
            showError('Error playing stream. Please check the URL and try again.');
            setIsStreamPlaying(false);
          }
        };

        streamAudioRef.current = audio;
      }

      // Set stream URL and play
      try {
        streamAudioRef.current.src = streamUrl;
        streamAudioRef.current.load();
        streamAudioRef.current
          .play()
          .then(() => {
            setIsStreamPlaying(true);
          })
          .catch(() => {
            showError('Error playing stream. Please check the URL and ensure CORS is enabled if needed.');
            setIsStreamPlaying(false);
          });
      } catch (error) {
        showError('Error setting stream source: ' + (error?.message || 'Unknown error'));
        setIsStreamPlaying(false);
      }
    },
    [audioUrl, isStreamPlaying, showError],
  );

  // Setup audio element effects
  useEffect(() => {
    if (!audioUrl) {
      return;
    }

    if (!audioRef.current) {
      const audio = new Audio();
      audio.crossOrigin = 'anonymous';
      audioRef.current = audio;
    }

    if (audioRef.current) {
      audioRef.current.crossOrigin = 'anonymous';
      audioRef.current.onended = () => {
        setIsPlaying(false);
        setPlayingFileIndex(null);
      };
      audioRef.current.onpause = () => {
        if (audioRef.current && audioRef.current.paused) {
          setIsPlaying(false);
        }
      };
      audioRef.current.onerror = () => {
        setIsPlaying(false);
        setPlayingFileIndex(null);
      };

      const currentSrc = audioRef.current.src || '';
      if (currentSrc !== audioUrl) {
        if (audioRef.current && !audioRef.current.paused) {
          audioRef.current.pause();
        }
        audioRef.current.src = audioUrl;
        audioRef.current.load();
      }

      if (playingFileIndex !== null) {
        const playAudio = async () => {
          if (!audioRef.current) return;
          try {
            if (audioRef.current.readyState >= 2) {
              await audioRef.current.play();
              setIsPlaying(true);
            } else {
              const canPlayHandler = async () => {
                try {
                  if (audioRef.current) {
                    await audioRef.current.play();
                    setIsPlaying(true);
                  }
                } catch (err) {
                  setIsPlaying(false);
                  setPlayingFileIndex(null);
                }
                if (audioRef.current) {
                  audioRef.current.removeEventListener('canplay', canPlayHandler);
                  audioRef.current.removeEventListener('loadeddata', canPlayHandler);
                }
              };
              audioRef.current.addEventListener('canplay', canPlayHandler);
              audioRef.current.addEventListener('loadeddata', canPlayHandler);
            }
          } catch (err) {
            setIsPlaying(false);
            setPlayingFileIndex(null);
          }
        };
        playAudio();
      }
    }
  }, [audioUrl, playingFileIndex]);

  // Setup chime audio element effects
  useEffect(() => {
    if (!chimeAudioUrl) {
      return;
    }

    if (!chimeAudioRef.current) {
      const audio = new Audio();
      audio.crossOrigin = 'anonymous';
      chimeAudioRef.current = audio;
    }

    if (chimeAudioRef.current) {
      chimeAudioRef.current.crossOrigin = 'anonymous';
      chimeAudioRef.current.onended = () => {
        setIsChimePlaying(false);
        setPlayingChimeIndex(null);
      };
      chimeAudioRef.current.onerror = () => {
        setIsChimePlaying(false);
        setPlayingChimeIndex(null);
      };

      const currentSrc = chimeAudioRef.current.src || '';
      if (currentSrc !== chimeAudioUrl) {
        chimeAudioRef.current.src = chimeAudioUrl;
        chimeAudioRef.current.load();
      }

      if (playingChimeIndex !== null) {
        const playAudio = async () => {
          if (!chimeAudioRef.current) return;
          try {
            await chimeAudioRef.current.play();
            setIsChimePlaying(true);
          } catch (err) {
            setIsChimePlaying(false);
            setPlayingChimeIndex(null);
          }
        };
        playAudio();
      }
    }
  }, [chimeAudioUrl, playingChimeIndex]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioUrl && audioUrl.startsWith('blob:')) {
        URL.revokeObjectURL(audioUrl);
      }
      if (chimeAudioUrl && chimeAudioUrl.startsWith('blob:')) {
        URL.revokeObjectURL(chimeAudioUrl);
      }
    };
  }, [audioUrl, chimeAudioUrl]);

  return {
    // Music audio
    audioUrl,
    isPlaying,
    playingFileIndex,
    audioRef,
    playFile,
    playServerFile,

    // Chime audio
    chimeAudioUrl,
    isChimePlaying,
    playingChimeIndex,
    chimeAudioRef,
    playChimeServerFile,

    // Stream audio
    isStreamPlaying,
    streamAudioRef,
    testStream,
  };
}

